import path from "path";
import { fileURLToPath } from "url";
import fs from "fs";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Header,
  Footer,
  PageNumber,
  ImageRun,
  AlignmentType,
  PageBreak,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
} from "docx";
import { createRequire } from "module";
const _require = createRequire(import.meta.url);
const PdfPrinter: new (fonts: any, vfs: any, urlResolver: any, localAccess: any) => any =
  (_require("pdfmake/js/Printer.js").default ?? _require("pdfmake/js/Printer.js"));
const _vfs: any = _require("pdfmake/js/virtual-fs.js").default ?? _require("pdfmake/js/virtual-fs.js");
const URLResolver: new (fs: any) => any =
  (_require("pdfmake/js/URLResolver.js").default ?? _require("pdfmake/js/URLResolver.js"));
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);
const FONTS_DIR = path.join(__dirname, "../../fonts");

export const TECHNICAL_SECTIONS = [
  { key: "introduction",       arLabel: "المقدمة",              enLabel: "Introduction" },
  { key: "scopeUnderstanding", arLabel: "فهم نطاق العمل",       enLabel: "Scope Understanding" },
  { key: "methodology",        arLabel: "المنهجية",             enLabel: "Methodology" },
  { key: "team",               arLabel: "الفريق",               enLabel: "Team" },
  { key: "timeline",           arLabel: "الجدول الزمني",        enLabel: "Timeline" },
  { key: "qualityManagement",  arLabel: "إدارة الجودة",         enLabel: "Quality Management" },
  { key: "riskManagement",     arLabel: "إدارة المخاطر",        enLabel: "Risk Management" },
  { key: "conclusion",         arLabel: "الخاتمة",              enLabel: "Conclusion" },
];

export const FINANCIAL_SECTIONS = [
  { key: "financialSummary",     arLabel: "الملخص المالي",      enLabel: "Financial Summary" },
  { key: "pricingTable",         arLabel: "جدول الأسعار",       enLabel: "Pricing Table" },
  { key: "paymentTerms",         arLabel: "شروط الدفع",         enLabel: "Payment Terms" },
  { key: "financialGuarantees",  arLabel: "الضمانات المالية",   enLabel: "Financial Guarantees" },
  { key: "totalAmount",          arLabel: "إجمالي المبلغ",      enLabel: "Total Amount" },
];

export interface ExportOptions {
  sectionNumbering?: boolean;
  withCoverPage?: boolean;
  sectionImages?: Record<string, Buffer>;
}

// ─── Markdown helpers ───────────────────────────────────────────────────────

/**
 * Parse a pipe-delimited Markdown-style table.
 * Returns null if `text` does not contain a recognisable table.
 */
function parseMarkdownTable(text: string): { headers: string[]; rows: string[][] } | null {
  if (!text) return null;
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const tableLines = lines.filter((l) => l.startsWith("|") && l.endsWith("|"));
  if (tableLines.length < 2) return null;

  const parseRow = (l: string) =>
    l.slice(1, -1).split("|").map((c) => c.trim());

  const headerCells = parseRow(tableLines[0]);
  // Skip separator row (------)
  const rows: string[][] = [];
  for (let i = 1; i < tableLines.length; i++) {
    const cells = parseRow(tableLines[i]);
    if (cells.every((c) => /^[-:\s]+$/.test(c))) continue;
    rows.push(cells);
  }
  if (rows.length === 0) return null;
  return { headers: headerCells, rows };
}

/** Strip the table portion of a string and return the remaining narrative text. */
function stripTable(text: string): string {
  if (!text) return "";
  return text
    .split("\n")
    .filter((l) => !(l.trim().startsWith("|") && l.trim().endsWith("|")))
    .join("\n")
    .trim();
}

// ─── DOCX export ────────────────────────────────────────────────────────────

function makeCellShading(color: string) {
  return { type: ShadingType.CLEAR, fill: color, color: "auto" };
}

function buildDocxPricingTable(parsed: { headers: string[]; rows: string[][] }, isAr: boolean): Table {
  const headerRow = new TableRow({
    tableHeader: true,
    children: parsed.headers.map((h) => new TableCell({
      shading: makeCellShading("1a3a5c"),
      children: [new Paragraph({
        children: [new TextRun({ text: h, bold: true, color: "FFFFFF", size: 20, font: "Arial" })],
        alignment: AlignmentType.CENTER,
        bidirectional: isAr,
      })],
    })),
  });

  const bodyRows = parsed.rows.map((row, i) => new TableRow({
    children: parsed.headers.map((_, idx) => {
      const cellText = row[idx] ?? "";
      const isNumeric = /^[\d,\.\sر.سSAR%-]+$/.test(cellText.trim()) && /\d/.test(cellText);
      const isTotalRow = /total|إجمالي|المجموع|مجموع|grand/i.test(row[0] || "");
      return new TableCell({
        shading: makeCellShading(isTotalRow ? "f0f4fa" : (i % 2 === 0 ? "ffffff" : "f8f9fb")),
        children: [new Paragraph({
          children: [new TextRun({
            text: cellText, size: 20, font: "Arial",
            bold: isTotalRow, color: isTotalRow ? "1a3a5c" : "222222",
          })],
          alignment: isNumeric ? AlignmentType.RIGHT : (isAr ? AlignmentType.RIGHT : AlignmentType.LEFT),
          bidirectional: isAr,
        })],
      });
    }),
  }));

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: "1a3a5c" },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "1a3a5c" },
      left: { style: BorderStyle.SINGLE, size: 2, color: "cccccc" },
      right: { style: BorderStyle.SINGLE, size: 2, color: "cccccc" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "dddddd" },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "dddddd" },
    },
    rows: [headerRow, ...bodyRows],
  });
}

export async function generateWordDocument(
  proposal: Record<string, any>,
  tenderTitle: string,
  companyName: string,
  type: "technical" | "financial",
  language: "ar" | "en",
  letterheadImageBuffer?: Buffer | null,
  options: ExportOptions = {},
): Promise<Buffer> {
  const isAr = language === "ar";
  const { sectionNumbering = true, withCoverPage = true, sectionImages = {} } = options;
  const sections = type === "technical" ? TECHNICAL_SECTIONS : FINANCIAL_SECTIONS;
  const docTitle =
    type === "technical"
      ? isAr ? "العرض الفني" : "Technical Proposal"
      : isAr ? "العرض المالي" : "Financial Proposal";
  const dateStr = new Date().toLocaleDateString(isAr ? "ar-SA" : "en-US");

  // Prefer structured pricing items when available — they're the source of
  // truth (server-computed VAT/totals). Fall back to markdown parsing.
  const structuredPricing = (() => {
    const pi = proposal.pricingItems;
    if (!pi || typeof pi !== "object") return null;
    const items = Array.isArray(pi.items) ? pi.items : [];
    if (items.length === 0) return null;
    return pi as { items: any[]; subtotal: number; vatRate: number; vatAmount: number; total: number };
  })();
  const renderPricingFromItems = (): { headers: string[]; rows: string[][] } | null => {
    if (!structuredPricing) return null;
    const headers = isAr
      ? ["#", "البند", "الكمية", "الوحدة", "سعر الوحدة (ر.س)", "الإجمالي (ر.س)"]
      : ["#", "Description", "Qty", "Unit", "Unit Price (SAR)", "Total (SAR)"];
    const fmt = (v: number) => Number(v ?? 0).toLocaleString(isAr ? "ar-SA" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const rows: string[][] = structuredPricing.items.map((it: any, i: number) => [
      String(i + 1), String(it.description ?? ""), fmt(Number(it.quantity ?? 0)),
      String(it.unit ?? ""), fmt(Number(it.unitPrice ?? 0)), fmt(Number(it.total ?? 0)),
    ]);
    const sub = isAr ? "الإجمالي قبل الضريبة" : "Subtotal";
    const vat = isAr
      ? `ضريبة القيمة المضافة (${Math.round((structuredPricing.vatRate ?? 0.15) * 100)}٪)`
      : `VAT (${Math.round((structuredPricing.vatRate ?? 0.15) * 100)}%)`;
    const tot = isAr ? "الإجمالي مع الضريبة" : "Grand Total (incl. VAT)";
    rows.push(["", sub, "", "", "", fmt(structuredPricing.subtotal)]);
    rows.push(["", vat, "", "", "", fmt(structuredPricing.vatAmount)]);
    rows.push(["", tot, "", "", "", fmt(structuredPricing.total)]);
    return { headers, rows };
  };

  const children: (Paragraph | Table)[] = [];

  // ── Cover page (skip if caller explicitly disabled it) ──
  if (withCoverPage) {
    for (let i = 0; i < 6; i++) children.push(new Paragraph({ text: "" }));
    children.push(new Paragraph({
      children: [new TextRun({ text: docTitle, bold: true, size: 72, color: "1a3a5c", font: "Arial" })],
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({ text: "" }));
    children.push(new Paragraph({
      children: [new TextRun({ text: tenderTitle, size: 40, color: "333333", font: "Arial" })],
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({ text: "" }));
    children.push(new Paragraph({
      children: [new TextRun({
        text: isAr ? `مقدم من: ${companyName}` : `Submitted by: ${companyName}`,
        size: 28, italics: true, color: "555555", font: "Arial",
      })],
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({ text: "" }));
    children.push(new Paragraph({
      children: [new TextRun({
        text: isAr ? `التاريخ: ${dateStr}` : `Date: ${dateStr}`,
        size: 24, color: "888888", font: "Arial",
      })],
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({ children: [new PageBreak()] }));
  }

  // ── Table of Contents ──
  children.push(new Paragraph({
    children: [new TextRun({
      text: isAr ? "جدول المحتويات" : "Table of Contents",
      bold: true, size: 36, color: "1a3a5c", font: "Arial",
    })],
    heading: HeadingLevel.HEADING_1,
    alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
    bidirectional: isAr,
  }));
  children.push(new Paragraph({ text: "" }));
  for (const [idx, { arLabel, enLabel }] of sections.entries()) {
    const label = isAr ? arLabel : enLabel;
    const num = sectionNumbering ? `${idx + 1}. ` : "";
    children.push(new Paragraph({
      children: [
        new TextRun({ text: `${num}${label}`, size: 24, color: "333333", font: "Arial" }),
      ],
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    }));
  }
  children.push(new Paragraph({ children: [new PageBreak()] }));

  // ── Sections body ──
  for (const [idx, { key, arLabel, enLabel }] of sections.entries()) {
    const baseLabel = isAr ? arLabel : enLabel;
    const num = idx + 1;
    const label = sectionNumbering ? `${num}. ${baseLabel}` : baseLabel;
    const content = proposal[key] || "";

    children.push(new Paragraph({
      children: [new TextRun({ text: label, bold: true, size: 30, color: "1a3a5c", font: "Arial" })],
      heading: HeadingLevel.HEADING_2,
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    }));

    // Try parsing as table for pricingTable / riskManagement.
    // For pricingTable, prefer the server-computed structured items.
    const fromStructured = key === "pricingTable" ? renderPricingFromItems() : null;
    const parsed = fromStructured
      ?? ((key === "pricingTable" || key === "riskManagement") ? parseMarkdownTable(content) : null);
    if (parsed) {
      const intro = fromStructured ? "" : stripTable(content);
      if (intro) {
        for (const line of intro.split("\n").filter((l) => l.trim())) {
          children.push(new Paragraph({
            children: [new TextRun({ text: line, size: 22, font: "Arial" })],
            alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidirectional: isAr,
          }));
        }
        children.push(new Paragraph({ text: "" }));
      }
      children.push(buildDocxPricingTable(parsed, isAr));
    } else if (content) {
      let subNum = 0;
      for (const rawLine of content.split("\n")) {
        const line = rawLine.trim();
        if (!line) { children.push(new Paragraph({ text: "" })); continue; }

        // Hierarchical numbering: lines beginning with "## " become "1.x" sub-headings
        if (/^##\s+/.test(line)) {
          subNum += 1;
          const subTitle = line.replace(/^##\s+/, "");
          const subLabel = sectionNumbering ? `${num}.${subNum} ${subTitle}` : subTitle;
          children.push(new Paragraph({
            children: [new TextRun({ text: subLabel, bold: true, size: 24, color: "2a4a7a", font: "Arial" })],
            heading: HeadingLevel.HEADING_3,
            alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidirectional: isAr,
          }));
          continue;
        }

        const bulletMatch = /^[-•*]\s+/.test(line);
        const text = bulletMatch ? line.replace(/^[-•*]\s+/, "") : line;
        children.push(new Paragraph({
          children: [new TextRun({ text, size: 22, font: "Arial" })],
          alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
          bidirectional: isAr,
          ...(bulletMatch ? { bullet: { level: 0 } } : {}),
        }));
      }
    } else {
      children.push(new Paragraph({
        children: [new TextRun({ text: isAr ? "(لا يوجد محتوى)" : "(No content)", size: 22, color: "999999", italics: true, font: "Arial" })],
        alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
        bidirectional: isAr,
      }));
    }

    const imgBuf = sectionImages[key];
    if (imgBuf) {
      try {
        children.push(new Paragraph({ text: "" }));
        children.push(new Paragraph({
          children: [new ImageRun({ data: imgBuf, transformation: { width: 400, height: 280 }, type: "png" } as any)],
          alignment: AlignmentType.CENTER,
        }));
        children.push(new Paragraph({
          children: [new TextRun({
            text: isAr ? `شكل ${num}: ${baseLabel}` : `Figure ${num}: ${baseLabel}`,
            size: 18, italics: true, color: "777777", font: "Arial",
          })],
          alignment: AlignmentType.CENTER,
        }));
      } catch { }
    }

    children.push(new Paragraph({ text: "" }));
  }

  // ── Signature page ──
  children.push(new Paragraph({ children: [new PageBreak()] }));
  children.push(new Paragraph({
    children: [new TextRun({ text: isAr ? "الإقرار والتوقيع" : "Acknowledgement & Signature", bold: true, size: 32, color: "1a3a5c", font: "Arial" })],
    heading: HeadingLevel.HEADING_1,
    alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
    bidirectional: isAr,
  }));
  children.push(new Paragraph({ text: "" }));
  children.push(new Paragraph({
    children: [new TextRun({
      text: isAr
        ? "نقرّ نحن مقدّمو العرض بأن جميع البيانات الواردة في هذا العرض صحيحة، وأننا ملتزمون بشروطه طوال فترة سريانه."
        : "We, the bidder, hereby certify that all information in this proposal is accurate and that we are bound by its terms for its full validity period.",
      size: 22, font: "Arial",
    })],
    alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
    bidirectional: isAr,
  }));
  for (let i = 0; i < 3; i++) children.push(new Paragraph({ text: "" }));
  for (const lbl of [
    isAr ? "اسم المفوّض بالتوقيع: ______________________" : "Authorized signatory name: ______________________",
    isAr ? "المنصب: ______________________" : "Title: ______________________",
    isAr ? "التوقيع: ______________________" : "Signature: ______________________",
    isAr ? "التاريخ: ______________________" : "Date: ______________________",
    isAr ? "الختم الرسمي:" : "Official stamp:",
  ]) {
    children.push(new Paragraph({
      children: [new TextRun({ text: lbl, size: 22, font: "Arial" })],
      alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidirectional: isAr,
    }));
    children.push(new Paragraph({ text: "" }));
  }

  const headerChildren: Paragraph[] = [];
  if (letterheadImageBuffer) {
    try {
      headerChildren.push(new Paragraph({
        children: [new ImageRun({ data: letterheadImageBuffer, transformation: { width: 580, height: 90 }, type: "png" } as any)],
        alignment: AlignmentType.CENTER,
      }));
    } catch { }
  }
  // Always include header text bar
  headerChildren.push(new Paragraph({
    children: [new TextRun({ text: `${companyName}  —  ${docTitle}`, size: 16, color: "666666", font: "Arial" })],
    alignment: isAr ? AlignmentType.RIGHT : AlignmentType.LEFT,
    bidirectional: isAr,
  }));

  const footer = new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({ text: `${companyName}  •  ${dateStr}  •  `, size: 16, color: "888888", font: "Arial" }),
          new TextRun({ text: isAr ? "صفحة " : "Page ", size: 16, color: "888888", font: "Arial" }),
          new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "888888", font: "Arial" }),
          new TextRun({ text: " / ", size: 16, color: "888888", font: "Arial" }),
          new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: "888888", font: "Arial" }),
        ],
        alignment: AlignmentType.CENTER,
      }),
    ],
  });

  const doc = new Document({
    sections: [{
      properties: withCoverPage ? { titlePage: true } : undefined,
      headers: withCoverPage
        ? { default: new Header({ children: headerChildren }), first: new Header({ children: [] }) }
        : { default: new Header({ children: headerChildren }) },
      footers: withCoverPage
        ? { default: footer, first: new Footer({ children: [] }) }
        : { default: footer },
      children,
    }],
  });

  return await Packer.toBuffer(doc);
}

// ─── PDF export ─────────────────────────────────────────────────────────────

function buildPdfPricingTable(parsed: { headers: string[]; rows: string[][] }, isAr: boolean) {
  const widths = parsed.headers.length <= 3 ? ["*", "*", "*"] : Array(parsed.headers.length).fill("*");
  const body: any[][] = [];
  body.push(parsed.headers.map((h) => ({
    text: h, bold: true, color: "#ffffff", fillColor: "#1a3a5c",
    alignment: "center", margin: [4, 6, 4, 6],
  })));
  for (let i = 0; i < parsed.rows.length; i++) {
    const row = parsed.rows[i];
    const isTotalRow = /total|إجمالي|المجموع|مجموع|grand/i.test(row[0] || "");
    body.push(parsed.headers.map((_, idx) => {
      const cellText = row[idx] ?? "";
      const isNumeric = /^[\d,\.\sر.سSAR%-]+$/.test(cellText.trim()) && /\d/.test(cellText);
      return {
        text: cellText,
        bold: isTotalRow,
        color: isTotalRow ? "#1a3a5c" : "#222222",
        fillColor: isTotalRow ? "#f0f4fa" : (i % 2 === 0 ? "#ffffff" : "#f8f9fb"),
        alignment: isNumeric ? "right" : (isAr ? "right" : "left"),
        margin: [4, 5, 4, 5],
      };
    }));
  }
  return {
    table: { headerRows: 1, widths, body, dontBreakRows: true },
    layout: {
      hLineWidth: (i: number, node: any) =>
        (i === 0 || i === node.table.body.length || i === 1) ? 1 : 0.5,
      vLineWidth: () => 0.5,
      hLineColor: (i: number) => (i === 1 ? "#1a3a5c" : "#dddddd"),
      vLineColor: () => "#dddddd",
    },
    margin: [0, 4, 0, 10],
  };
}

export async function generatePdfDocument(
  proposal: Record<string, any>,
  tenderTitle: string,
  companyName: string,
  type: "technical" | "financial",
  language: "ar" | "en",
  letterheadImageBuffer?: Buffer | null,
  options: ExportOptions = {},
): Promise<Buffer> {
  const isAr = language === "ar";
  const { sectionNumbering = true, withCoverPage = true, sectionImages = {} } = options;
  const sections = type === "technical" ? TECHNICAL_SECTIONS : FINANCIAL_SECTIONS;
  const docTitle =
    type === "technical"
      ? isAr ? "العرض الفني" : "Technical Proposal"
      : isAr ? "العرض المالي" : "Financial Proposal";
  const dateStr = new Date().toLocaleDateString(isAr ? "ar-SA" : "en-US");
  const ALIGN = isAr ? "right" : "left";

  // Structured pricing source of truth — see DOCX builder above for rationale.
  const structuredPricing = (() => {
    const pi = proposal.pricingItems;
    if (!pi || typeof pi !== "object") return null;
    const items = Array.isArray(pi.items) ? pi.items : [];
    if (items.length === 0) return null;
    return pi as { items: any[]; subtotal: number; vatRate: number; vatAmount: number; total: number };
  })();
  const renderPricingFromItems = (): { headers: string[]; rows: string[][] } | null => {
    if (!structuredPricing) return null;
    const headers = isAr
      ? ["#", "البند", "الكمية", "الوحدة", "سعر الوحدة (ر.س)", "الإجمالي (ر.س)"]
      : ["#", "Description", "Qty", "Unit", "Unit Price (SAR)", "Total (SAR)"];
    const fmt = (v: number) => Number(v ?? 0).toLocaleString(isAr ? "ar-SA" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const rows: string[][] = structuredPricing.items.map((it: any, i: number) => [
      String(i + 1), String(it.description ?? ""), fmt(Number(it.quantity ?? 0)),
      String(it.unit ?? ""), fmt(Number(it.unitPrice ?? 0)), fmt(Number(it.total ?? 0)),
    ]);
    const sub = isAr ? "الإجمالي قبل الضريبة" : "Subtotal";
    const vat = isAr
      ? `ضريبة القيمة المضافة (${Math.round((structuredPricing.vatRate ?? 0.15) * 100)}٪)`
      : `VAT (${Math.round((structuredPricing.vatRate ?? 0.15) * 100)}%)`;
    const tot = isAr ? "الإجمالي مع الضريبة" : "Grand Total (incl. VAT)";
    rows.push(["", sub, "", "", "", fmt(structuredPricing.subtotal)]);
    rows.push(["", vat, "", "", "", fmt(structuredPricing.vatAmount)]);
    rows.push(["", tot, "", "", "", fmt(structuredPricing.total)]);
    return { headers, rows };
  };

  const fontTtfPath = path.join(FONTS_DIR, "Cairo-Regular.ttf");
  const fontBuffer = fs.existsSync(fontTtfPath) ? fs.readFileSync(fontTtfPath) : null;
  const fontDef = fontBuffer
    ? { Cairo: { normal: fontBuffer, bold: fontBuffer, italics: fontBuffer, bolditalics: fontBuffer } }
    : { Roboto: { normal: "Helvetica", bold: "Helvetica-Bold", italics: "Helvetica-Oblique", bolditalics: "Helvetica-BoldOblique" } };
  const mainFont = fontBuffer ? "Cairo" : "Roboto";

  const urlResolver = new URLResolver(_vfs);
  urlResolver.setUrlAccessPolicy(() => false);
  const printer = new PdfPrinter(fontDef, _vfs, urlResolver, () => true);

  const headerLogoImage = letterheadImageBuffer
    ? { image: `data:image/png;base64,${letterheadImageBuffer.toString("base64")}`, width: 475, alignment: "center" as const }
    : null;

  const content: any[] = [];

  // ── Cover page (skip when caller disables it) ──
  if (withCoverPage) {
    if (headerLogoImage) content.push({ ...headerLogoImage, margin: [0, 0, 0, 60] });
    else content.push({ text: "", margin: [0, 80, 0, 0] });
    content.push({ text: docTitle, fontSize: 32, bold: true, color: "#1a3a5c", alignment: "center", margin: [0, 60, 0, 20] });
    content.push({
      canvas: [{ type: "line", x1: 100, y1: 0, x2: 375, y2: 0, lineWidth: 2, lineColor: "#1a3a5c" }],
      margin: [0, 0, 0, 20],
    });
    content.push({ text: tenderTitle, fontSize: 18, color: "#333333", alignment: "center", margin: [0, 0, 0, 30] });
    content.push({
      text: isAr ? `مقدم من` : `Submitted by`,
      fontSize: 11, color: "#888888", alignment: "center", margin: [0, 30, 0, 4],
    });
    content.push({ text: companyName, fontSize: 16, bold: true, color: "#1a3a5c", alignment: "center", margin: [0, 0, 0, 30] });
    content.push({
      text: isAr ? `التاريخ: ${dateStr}` : `Date: ${dateStr}`,
      fontSize: 12, color: "#555555", alignment: "center",
    });
    content.push({ text: "", pageBreak: "after" });
  }

  // ── Table of Contents ──
  content.push({ text: isAr ? "جدول المحتويات" : "Table of Contents", fontSize: 20, bold: true, color: "#1a3a5c", alignment: ALIGN, margin: [0, 0, 0, 12] });
  content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 475, y2: 0, lineWidth: 1, lineColor: "#1a3a5c" }], margin: [0, 0, 0, 14] });
  for (const [idx, { arLabel, enLabel }] of sections.entries()) {
    const num = sectionNumbering ? `${idx + 1}. ` : "";
    content.push({
      text: `${num}${isAr ? arLabel : enLabel}`,
      fontSize: 12, color: "#333333", alignment: ALIGN, margin: [10, 4, 10, 4],
    });
  }
  content.push({ text: "", pageBreak: "after" });

  // ── Sections body ──
  for (const [idx, { key, arLabel, enLabel }] of sections.entries()) {
    const baseLabel = isAr ? arLabel : enLabel;
    const num = idx + 1;
    const label = sectionNumbering ? `${num}. ${baseLabel}` : baseLabel;
    const rawContent = proposal[key] || "";

    content.push({ text: label, fontSize: 15, bold: true, color: "#1a3a5c", alignment: ALIGN, margin: [0, 12, 0, 4] });
    content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 1.5, lineColor: "#1a3a5c" }], margin: [0, 0, 0, 8] });

    // Try parsing as a table for pricingTable / riskManagement.
    // For pricingTable, prefer server-computed structured items.
    const fromStructured = key === "pricingTable" ? renderPricingFromItems() : null;
    const parsed = fromStructured
      ?? ((key === "pricingTable" || key === "riskManagement") ? parseMarkdownTable(rawContent) : null);
    if (parsed) {
      const intro = fromStructured ? "" : stripTable(rawContent);
      if (intro) {
        content.push({ text: intro, fontSize: 11, color: "#222222", alignment: ALIGN, lineHeight: 1.5, margin: [0, 0, 0, 6] });
      }
      content.push(buildPdfPricingTable(parsed, isAr));
    } else if (rawContent) {
      // Hierarchical sub-headings (## ...) → "n.x"
      let subNum = 0;
      const blocks: any[] = [];
      const lines = rawContent.split("\n");
      let buf: string[] = [];
      const flush = () => {
        if (buf.length) {
          blocks.push({
            text: buf.join("\n"), fontSize: 11, color: "#222222",
            alignment: ALIGN, lineHeight: 1.45, margin: [0, 0, 0, 6],
          });
          buf = [];
        }
      };
      for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        if (/^##\s+/.test(line.trim())) {
          flush();
          subNum += 1;
          const sub = line.trim().replace(/^##\s+/, "");
          const subLabel = sectionNumbering ? `${num}.${subNum} ${sub}` : sub;
          blocks.push({ text: subLabel, fontSize: 12, bold: true, color: "#2a4a7a", alignment: ALIGN, margin: [0, 6, 0, 4] });
        } else if (/^[-•*]\s+/.test(line.trim())) {
          flush();
          blocks.push({
            ul: [{ text: line.trim().replace(/^[-•*]\s+/, ""), fontSize: 11, color: "#222222" }],
            margin: [0, 0, 0, 4],
          } as any);
        } else {
          buf.push(line);
        }
      }
      flush();
      content.push(...blocks);
    } else {
      content.push({ text: isAr ? "(لا يوجد محتوى)" : "(No content)", fontSize: 11, italics: true, color: "#999999", alignment: ALIGN, margin: [0, 0, 0, 6] });
    }

    const imgBuf = sectionImages[key];
    if (imgBuf) {
      try {
        const b64 = imgBuf.toString("base64");
        content.push({ image: `data:image/png;base64,${b64}`, width: 320, alignment: "center", margin: [0, 6, 0, 4] });
        content.push({
          text: isAr ? `شكل ${num}: ${baseLabel}` : `Figure ${num}: ${baseLabel}`,
          fontSize: 9, italics: true, color: "#888888", alignment: "center", margin: [0, 0, 0, 10],
        });
      } catch { }
    }
  }

  // ── Signature page ──
  content.push({ text: "", pageBreak: "before" });
  content.push({ text: isAr ? "الإقرار والتوقيع" : "Acknowledgement & Signature", fontSize: 18, bold: true, color: "#1a3a5c", alignment: ALIGN, margin: [0, 0, 0, 12] });
  content.push({ canvas: [{ type: "line", x1: 0, y1: 0, x2: 475, y2: 0, lineWidth: 1, lineColor: "#1a3a5c" }], margin: [0, 0, 0, 16] });
  content.push({
    text: isAr
      ? "نقرّ نحن مقدّمو العرض بأن جميع البيانات الواردة في هذا العرض صحيحة، وأننا ملتزمون بشروطه طوال فترة سريانه."
      : "We, the bidder, hereby certify that all information in this proposal is accurate and that we are bound by its terms for its full validity period.",
    fontSize: 11, color: "#222222", alignment: ALIGN, lineHeight: 1.5, margin: [0, 0, 0, 30],
  });
  for (const lbl of [
    isAr ? "اسم المفوّض بالتوقيع: ______________________" : "Authorized signatory name: ______________________",
    isAr ? "المنصب: ______________________" : "Title: ______________________",
    isAr ? "التوقيع: ______________________" : "Signature: ______________________",
    isAr ? "التاريخ: ______________________" : "Date: ______________________",
    isAr ? "الختم الرسمي:" : "Official stamp:",
  ]) {
    content.push({ text: lbl, fontSize: 11, color: "#444444", alignment: ALIGN, margin: [0, 10, 0, 10] });
  }

  const docDef: any = {
    content,
    defaultStyle: { font: mainFont, fontSize: 11 },
    pageSize: "A4",
    pageMargins: [55, 90, 55, 55],
    // Running header (skipped on the cover page when it's present)
    header: (currentPage: number) => {
      if (withCoverPage && currentPage === 1) return undefined;
      const stack: any[] = [];
      if (headerLogoImage) {
        stack.push({ image: headerLogoImage.image, width: 180, alignment: "center", margin: [0, 14, 0, 4] });
      } else {
        stack.push({ text: companyName, fontSize: 10, color: "#666666", alignment: "center", margin: [0, 22, 0, 4] });
      }
      stack.push({
        text: docTitle,
        fontSize: 9, color: "#888888", alignment: "center", margin: [0, 0, 0, 4],
      });
      stack.push({ canvas: [{ type: "line", x1: 55, y1: 0, x2: 540, y2: 0, lineWidth: 0.5, lineColor: "#cccccc" }] });
      return stack;
    },
    footer: (currentPage: number, pageCount: number) => {
      if (withCoverPage && currentPage === 1) return undefined;
      return {
        columns: [
          { text: `${companyName}`, fontSize: 8, color: "#aaaaaa", alignment: "left", margin: [55, 10, 0, 0] },
          {
            text: `${isAr ? "صفحة" : "Page"} ${currentPage} / ${pageCount}`,
            fontSize: 8, color: "#aaaaaa", alignment: "center", margin: [0, 10, 0, 0],
          },
          { text: dateStr, fontSize: 8, color: "#aaaaaa", alignment: "right", margin: [0, 10, 55, 0] },
        ],
      };
    },
  };

  const pdfDocPromise = printer.createPdfKitDocument(docDef);
  const pdfDoc = await pdfDocPromise;
  return new Promise((resolve, reject) => {
    try {
      const chunks: Buffer[] = [];
      pdfDoc.on("data", (chunk: Buffer) => chunks.push(chunk));
      pdfDoc.on("end", () => resolve(Buffer.concat(chunks)));
      pdfDoc.on("error", reject);
      pdfDoc.end();
    } catch (e) {
      reject(e);
    }
  });
}

// ─── Word template (docxtemplater) ──────────────────────────────────────────

export interface TemplateData {
  tender_title: string;
  company_name: string;
  date: string;
  introduction: string;
  scope_understanding: string;
  methodology: string;
  team: string;
  timeline: string;
  quality_management: string;
  risk_management: string;
  conclusion: string;
  financial_summary: string;
  pricing_table: string;
  payment_terms: string;
  financial_guarantees: string;
  total_amount: string;
  [key: string]: string;
}

export function buildTemplateData(
  proposal: Record<string, any>,
  tenderTitle: string,
  companyName: string,
  language: "ar" | "en",
  sectionNumbering = true,
): TemplateData {
  const isAr = language === "ar";
  const technicalLabels = sectionNumbering
    ? TECHNICAL_SECTIONS.map((s, i) => ({ key: s.key, label: `${i + 1}. ${isAr ? s.arLabel : s.enLabel}` }))
    : TECHNICAL_SECTIONS.map((s) => ({ key: s.key, label: isAr ? s.arLabel : s.enLabel }));
  const financialLabels = sectionNumbering
    ? FINANCIAL_SECTIONS.map((s, i) => ({ key: s.key, label: `${i + 1}. ${isAr ? s.arLabel : s.enLabel}` }))
    : FINANCIAL_SECTIONS.map((s) => ({ key: s.key, label: isAr ? s.arLabel : s.enLabel }));

  const labelOf = (sectionKey: string) => {
    const found = [...technicalLabels, ...financialLabels].find((x) => x.key === sectionKey);
    return found?.label ?? sectionKey;
  };

  return {
    tender_title: tenderTitle,
    company_name: companyName,
    date: new Date().toLocaleDateString(isAr ? "ar-SA" : "en-US"),
    introduction:         proposal.introduction          ?? "",
    scope_understanding:  proposal.scopeUnderstanding    ?? "",
    methodology:          proposal.methodology           ?? "",
    team:                 proposal.team                  ?? "",
    timeline:             proposal.timeline              ?? "",
    quality_management:   proposal.qualityManagement     ?? "",
    risk_management:      proposal.riskManagement        ?? "",
    conclusion:           proposal.conclusion            ?? "",
    financial_summary:    proposal.financialSummary      ?? "",
    pricing_table:        proposal.pricingTable          ?? "",
    payment_terms:        proposal.paymentTerms          ?? "",
    financial_guarantees: proposal.financialGuarantees   ?? "",
    total_amount:         proposal.totalAmount           ?? "",
    label_introduction:         labelOf("introduction"),
    label_scope_understanding:  labelOf("scopeUnderstanding"),
    label_methodology:          labelOf("methodology"),
    label_team:                 labelOf("team"),
    label_timeline:             labelOf("timeline"),
    label_quality_management:   labelOf("qualityManagement"),
    label_risk_management:      labelOf("riskManagement"),
    label_conclusion:           labelOf("conclusion"),
    label_financial_summary:    labelOf("financialSummary"),
    label_pricing_table:        labelOf("pricingTable"),
    label_payment_terms:        labelOf("paymentTerms"),
    label_financial_guarantees: labelOf("financialGuarantees"),
    label_total_amount:         labelOf("totalAmount"),
  };
}

export async function generateWordFromTemplate(
  templateBuffer: Buffer,
  data: TemplateData,
): Promise<Buffer> {
  const zip = new PizZip(templateBuffer);
  const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true } as any);
  doc.render(data);
  return doc.getZip().generate({ type: "nodebuffer", compression: "DEFLATE" }) as Buffer;
}

export const TEMPLATE_PLACEHOLDERS = [
  { key: "{tender_title}",        arLabel: "عنوان المناقصة" },
  { key: "{company_name}",        arLabel: "اسم الشركة" },
  { key: "{date}",                arLabel: "التاريخ" },
  { key: "{introduction}",        arLabel: "المقدمة" },
  { key: "{scope_understanding}", arLabel: "فهم نطاق العمل" },
  { key: "{methodology}",         arLabel: "المنهجية" },
  { key: "{team}",                arLabel: "الفريق" },
  { key: "{timeline}",            arLabel: "الجدول الزمني" },
  { key: "{quality_management}",  arLabel: "إدارة الجودة" },
  { key: "{risk_management}",     arLabel: "إدارة المخاطر" },
  { key: "{conclusion}",          arLabel: "الخاتمة" },
  { key: "{financial_summary}",   arLabel: "الملخص المالي" },
  { key: "{pricing_table}",       arLabel: "جدول الأسعار" },
  { key: "{payment_terms}",       arLabel: "شروط الدفع" },
  { key: "{financial_guarantees}",arLabel: "الضمانات المالية" },
  { key: "{total_amount}",        arLabel: "إجمالي المبلغ" },
  { key: "{label_introduction}",         arLabel: "رقم وعنوان: المقدمة" },
  { key: "{label_scope_understanding}",  arLabel: "رقم وعنوان: فهم النطاق" },
  { key: "{label_methodology}",          arLabel: "رقم وعنوان: المنهجية" },
];
